// pages/DisclosureBuilder.jsx

import { useState, useEffect, useCallback } from "react";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import { getStoredRole } from "../../../shared/utils/auth";
import "../../../styles/pages/disclouserbuilder.css";
import { Link } from "react-router-dom";
import Toast from "../../../shared/components/Toast";

// ── Helpers ────────────────────────────────────────────────────────────────

const getQuestionValue = (q) => {
  if (q.numeric_value !== null && q.numeric_value !== undefined)
    return String(q.numeric_value);
  if (q.text_value !== null && q.text_value !== undefined && q.text_value !== "")
    return q.text_value;
  return "";
};

const TypeBadge = ({ dataType }) => {
  if (dataType === "numeric") {
    return <span className="db-type-badge db-type-badge--auto">Auto</span>;
  }
  return (
    <span className="db-type-badge db-type-badge--text">
      <span className="db-type-badge__icon">✏</span> Text
    </span>
  );
};

const StatusBadge = ({ displayStatus, completionPct }) => {
  const isComplete = completionPct === 100;
  return isComplete ? (
    <span className="db-status db-status--complete">
      <span className="db-status__dot" />
      Complete
    </span>
  ) : (
    <span className="db-status db-status--partial">
      <span className="db-status__dot" />
      {displayStatus}
    </span>
  );
};

const SectionRow = ({ section, onFieldChange, canEdit }) => {
  const [open, setOpen] = useState(false);

  const sectionLabel = section.is_principle
    ? `Section C — ${section.principle_code}`
    : `Section ${section.principle_code}`;

  // A field is editable only when ALL conditions pass
  const sectionEditable = canEdit && section.is_principle === 0 && section.is_locked !== 1;

  return (
    <div className="db-section">

      {/* Accordion Header */}
      <div className="db-section__header" onClick={() => setOpen((p) => !p)}>
        <div className="db-section__left">
          <span className="db-section__id">{sectionLabel}</span>
          <span className="db-section__divider">—</span>
          <span className="db-section__name">{section.principle_name}</span>
          {section.is_locked === 1 && (
            <span className="db-section__lock" title="Locked">🔒</span>
          )}
        </div>
        <div className="db-section__right">
          <StatusBadge
            displayStatus={section.display_status}
            completionPct={section.completion_percentage}
          />
          <span className={`db-section__chevron ${open ? "db-section__chevron--open" : ""}`}>
            ▲
          </span>
        </div>
      </div>

      {/* Accordion Body — Table */}
      {open && (
        <div className="db-section__body">
          <table className="db-table">
            <thead>
              <tr>
                <th className="db-table__th db-table__th--ref">REF</th>
                <th className="db-table__th db-table__th--question">SEBI DISCLOSURE REQUIREMENT</th>
                <th className="db-table__th db-table__th--response">RESPONSE</th>
                <th className="db-table__th db-table__th--type">TYPE</th>
              </tr>
            </thead>
            <tbody>
              {section.questions.map((q) => (
                <tr key={q.question_id} className="db-table__row">

                  {/* REF */}
                  <td className="db-table__td db-table__td--ref">
                    {q.question_code}
                  </td>

                  {/* Question text */}
                  <td className="db-table__td db-table__td--question">
                    {q.question_text}
                  </td>

                  {/* Response input */}
                  <td className="db-table__td db-table__td--response">
                    {q.data_type === "numeric" ? (
                      <input
                        type="number"
                        className="db-input"
                        defaultValue={getQuestionValue(q)}
                        placeholder="Enter value"
                        disabled={!sectionEditable}
                        style={{ background: !sectionEditable ? "#f5f5f5" : "white" }}
                        onChange={(e) =>
                          onFieldChange(
                            section.principle_id,
                            q.question_id,
                            e.target.value
                          )
                        }
                      />
                    ) : (
                      <input
                        type="text"
                        className="db-input"
                        defaultValue={getQuestionValue(q)}
                        placeholder="Enter response"
                        disabled={!sectionEditable}
                        style={{ background: !sectionEditable ? "#f5f5f5" : "white" }}
                        onChange={(e) =>
                          onFieldChange(
                            section.principle_id,
                            q.question_id,
                            e.target.value
                          )
                        }
                      />
                    )}
                  </td>

                  {/* Type badge */}
                  <td className="db-table__td db-table__td--type">
                    <TypeBadge dataType={q.data_type} />
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Loading Skeleton ───────────────────────────────────────────────────────
const LoadingSkeleton = () => (
  <div className="db-loading">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="db-section db-section--skeleton">
        <div className="db-skeleton-line db-skeleton-line--wide" />
        <div className="db-skeleton-line db-skeleton-line--narrow" />
      </div>
    ))}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────
const DisclosureBuilder = () => {
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => setToast({ message, type });

  const { reportingYear } = useReportingYear();
  const role = getStoredRole();

  const [sections,      setSections]      = useState([]);
  const [summary,       setSummary]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [changedFields, setChangedFields] = useState({});

  // ── Step 1 — is_locked state ──────────────────────────────────────────
  const [isYearLocked, setIsYearLocked] = useState(true); // default locked until API confirms

  // ── Step 2 — fetch is_locked for the selected reporting year ──────────
  useEffect(() => {
    const fetchYearLockStatus = async () => {
      try {
        const res = await apiClient.post("/api/reportingyear");
        const years = res.data?.data ?? [];

        const normalise = (val) =>
          (val ?? "").toString().trim().replace(/^fy/i, "").toLowerCase();

        const matched = years.find(
          (y) => normalise(y.year_label) === normalise(reportingYear)
        );

        // is_locked: 0 = not locked (editable), 1 = locked (read-only)
        setIsYearLocked(matched ? matched.is_locked === 1 : true);
      } catch (err) {
        console.error("fetchYearLockStatus error:", err);
        setIsYearLocked(true); // fail safe: stay read-only
      }
    };

    if (reportingYear) fetchYearLockStatus();
  }, [reportingYear]);

  // ── Step 3 — canEdit: role + year not locked ──────────────────────────
  // NOTE: is_principle === 0 check is handled per-section inside SectionRow
  const isAllowedRole = role === "Admin" || role === "Super Admin";
  const canEdit       = isAllowedRole && !isYearLocked;

  // ── Fetch disclosure data ─────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!reportingYear) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get("/api/brsr/disclosure-builder", {
        params: { reporting_year: reportingYear },
      });
      setSections(response.data?.data   ?? []);
      setSummary(response.data?.summary ?? null);
    } catch {
     const msg = "Failed to load disclosure data. Please try again.";
      setError(msg);
      showToast(msg, "error");
      setSections([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [reportingYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Track field edits ─────────────────────────────────────────────────
  const handleFieldChange = (principleId, questionId, value) => {
    setChangedFields((prev) => ({
      ...prev,
      [`${principleId}_${questionId}`]: { principleId, questionId, value },
    }));
  };

  // ── Save Draft ────────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    const payload = Object.values(changedFields);
    if (payload.length === 0) return;

    try {
      const principlesMap = {};

      payload.forEach((item) => {
        const principle_id = item.principleId;
        if (!principlesMap[principle_id]) {
          principlesMap[principle_id] = { principle_id, responses: [] };
        }
        principlesMap[principle_id].responses.push({
          response_id:   item.responseId || 0,
          question_id:   item.questionId,
          numeric_value: null,
          text_value:    item.value,
        });
      });

      const res = await apiClient.post("/api/brsr/disclosure-builder-save", {
        reporting_year: reportingYear,
        principles:     Object.values(principlesMap),
      });

      showToast(res.data?.message || "Draft saved successfully.", "success");
      setChangedFields({});
      fetchData();
    } catch (error) {
      console.error(error);
      showToast(error?.response?.data?.message || "Failed to save draft.", "error");
    }
  };

  // ── Derived totals ────────────────────────────────────────────────────
  const totalFields     = summary?.total_fields          ?? 0;
  const completedFields = summary?.completed             ?? 0;
  const completionPct   = summary?.completion_percentage ?? 0;
  const lockedSections  = summary?.locked_sections       ?? 0;
  const hasPendingEdits = Object.keys(changedFields).length > 0;

  return (
    <div className="db-page">

      {/* Page Header */}
      <div className="db-page-title">BRSR Disclosure (SEBI Format)</div>
      <div className="db-page-subtitle">
        Assemble the final BRSR report in SEBI format from approved data
      </div>
      <div className="db-reporting-bar">
        <strong>Reporting Year: {reportingYear ?? "—"}</strong>
        &nbsp;·&nbsp; Auto-generated from approved data for the selected reporting year.
      </div>

      {/* Read-only banner when user cannot edit */}
      {!canEdit && (
        <div
          style={{
            background: "#fff3cd",
            color: "#856404",
            padding: "8px 14px",
            borderRadius: "6px",
            fontSize: "13px",
            marginBottom: "12px",
          }}
        >
          🔒 Read-only —{" "}
          {!isAllowedRole
            ? `Your role (${role}) does not have edit access`
            : "Reporting year is locked and cannot be edited"}
        </div>
      )}

      {/* Summary Cards */}
      <div className="db-summary-row">
        {[
          { label: "Total Fields",    value: totalFields,         icon: null },
          { label: "Completed",       value: completedFields,     icon: null },
          { label: "Completion",      value: `${completionPct}%`, icon: null },
          { label: "Locked Sections", value: lockedSections,      icon: "🔒" },
        ].map(({ label, value, icon }) => (
          <div key={label} className="db-summary-card">
            <span className="db-summary-card__label">{label}</span>
            <span className="db-summary-card__value">
              {icon && <span className="db-summary-card__value-icon">{icon}</span>}
              {loading ? "—" : value}
            </span>
          </div>
        ))}
      </div>

      {/* Section Accordions */}
      <div className="db-sections">
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="db-error">{error}</div>
        ) : sections.length === 0 ? (
          <div className="db-empty">
            No disclosure data found for {reportingYear}.
          </div>
        ) : (
          sections.map((section) => (
            <SectionRow
              key={section.principle_id}
              section={section}
              onFieldChange={handleFieldChange}
              canEdit={canEdit}
            />
          ))
        )}
      </div>

      {/* Footer — Save Draft hidden when read-only */}
      <div className="db-footer">
        <a href="/dashboard/approval-lock" className="db-back-link">← Back to Approval &amp; Lock</a>
        <div className="db-footer__buttons">
          {canEdit && (
            <button
              className="db-btn-draft"
              onClick={handleSaveDraft}
              disabled={!hasPendingEdits}
            >
              📄 Save Draft {hasPendingEdits ? `(${Object.keys(changedFields).length})` : ""}
            </button>
          )}

           <Link to="/dashboard/export" className="al-btn-proceed">
            Generate Disclosure →
          </Link>

        </div>
      </div>

          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}
    </div>
  );
};

export default DisclosureBuilder;
